import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Link as MuiLink,
  Alert,
  InputAdornment
} from '@mui/material';
import {
  MarkEmailRead as MarkEmailReadIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';

import { useAuth } from '../../context/AuthContext';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const { verifyEmail, resendVerification } = useAuth();
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifyComplete, setVerifyComplete] = useState(false);
  const [verifyError, setVerifyError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm();

  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);

  useEffect(() => {
    const runVerification = async () => {
      if (!token) {
        return;
      }

      setIsVerifying(true);
      setVerifyError('');
      const result = await verifyEmail(token);
      setIsVerifying(false);

      if (result.success) {
        setVerifyComplete(true);
      } else {
        setVerifyError(result.error || 'Verification link is invalid or expired.');
      }
    };

    runVerification();
  }, [token, verifyEmail]);

  const onResend = async (data) => {
    await resendVerification(data.email);
  };

  return (
    <Box sx={{ maxWidth: 420, mx: 'auto' }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            textAlign: 'center',
            mb: 1,
            color: 'text.primary'
          }}
        >
          Verify Email
        </Typography>

        <Typography
          variant="body1"
          sx={{ textAlign: 'center', mb: 3, color: 'text.secondary' }}
        >
          Confirm your account email to activate full access.
        </Typography>

        {isVerifying && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Verifying your email token...
          </Alert>
        )}

        {verifyComplete && (
          <Alert severity="success" sx={{ mb: 3 }} icon={<MarkEmailReadIcon />}>
            Email verified successfully. You can continue to sign in.
          </Alert>
        )}

        {!!verifyError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {verifyError}
          </Alert>
        )}

        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          Need a new verification link?
        </Typography>

        <Box component="form" onSubmit={handleSubmit(onResend)}>
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            {...register('email', {
              required: 'Email is required',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Invalid email address'
              }
            })}
            error={!!errors.email}
            helperText={errors.email?.message}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon color="action" />
                </InputAdornment>
              )
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mb: 2, py: 1.4, fontWeight: 600 }}
          >
            Resend Verification Email
          </Button>

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Already verified?{' '}
              <MuiLink
                component={Link}
                to="/login"
                sx={{
                  fontWeight: 600,
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                Sign in
              </MuiLink>
            </Typography>
          </Box>
        </Box>
      </motion.div>
    </Box>
  );
};

export default VerifyEmail;
